/**
 * Painéis de origem de aquisição (Trilha F, F5/F6): o relatório por origem em Canais e os cards
 * "principal origem" do dashboard. Não são telas novas — são seções das telas existentes.
 *
 * Regras de honestidade (as mesmas de `orders/acquisition.ts`):
 *  - "Não informada" aparece como linha própria e nunca como líder: é o que falta atribuir;
 *  - conversão só existe quando há contagem de contatos da origem; sem ela a célula diz "—";
 *  - sem nenhuma venda atribuída os cards dizem "Sem dados", não zero;
 *  - o aviso de vendas sem origem leva para a lista de pedidos já filtrada nelas.
 */
import { createElement as h, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { formatBrl, formatCount, formatPercent } from "../format.js";
import {
  UNSET_ORIGIN_KEY,
  acquisitionBuckets,
  acquisitionCsv,
  leadingOrigin,
  strategyBuckets,
  unsetSales,
  type SalesOriginOption
} from "../orders/acquisition.js";
import type { PlatformUnifiedOrder } from "../api/types.js";
import { Button, Card, CardHeader, EmptyState } from "../ui/index.js";

export interface AcquisitionPanelsProps {
  orders: readonly PlatformUnifiedOrder[];
  /** Cadastro de origens (rótulos). Ausente = o slug serve de rótulo. */
  options?: readonly SalesOriginOption[];
  /** Conversas por origem no período; `null`/ausente = sem contagem (conversão fica "—"). */
  contactsByOrigin?: Readonly<Record<string, number>> | null;
}

const pct = (value: number | null): string => (value === null ? "—" : formatPercent(value * 100));

/** Baixa o CSV no navegador (BOM para o Excel reconhecer acentos). Só roda em clique, nunca no render. */
function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([String.fromCharCode(0xfeff), csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Comparativo por origem + estratégias, com CSV. Vive em Canais de venda. */
export function AcquisitionReport({ orders, options, contactsByOrigin }: AcquisitionPanelsProps): ReactElement {
  const buckets = acquisitionBuckets(orders, { options, contactsByOrigin: contactsByOrigin ?? undefined });
  const strategies = strategyBuckets(orders);
  const missing = unsetSales(buckets);
  const leaders = leadingOrigin(buckets);
  const hasContacts = contactsByOrigin !== null && contactsByOrigin !== undefined;
  return h(
    "section",
    { className: "flex flex-col gap-4", "data-section": "origem-aquisicao-relatorio" },
    h("h2", { className: "text-base font-extrabold text-ink" }, "Por origem do contato (de onde vêm as vendas)"),
    missing.totalSales > 0 && missing.sales > 0
      ? h(
          "p",
          { role: "status", className: "m-0 rounded-panel border border-warning bg-warning-soft p-3 text-sm text-ink", "data-acquisition": "sem-origem" },
          `${formatCount(missing.sales)} de ${formatCount(missing.totalSales)} venda(s) do período ainda não têm origem informada. `,
          h(Link, { to: `/pedidos?origem=${UNSET_ORIGIN_KEY}`, className: "font-bold text-brand-text" }, "Marcar a origem nos pedidos")
        )
      : null,
    buckets.length === 0
      ? h(EmptyState, { title: "Nenhuma venda no período", description: "Escolha outro período ou registre vendas para comparar as origens." })
      : h(
          Card,
          { "data-panel": "origem-aquisicao" },
          h(CardHeader, {
            title: "Comparativo por origem",
            description: leaders.bySales
              ? `Principal origem em vendas: ${leaders.bySales.label}. ${hasContacts ? "Conversão = vendas ÷ contatos da origem." : "Conversão indisponível: o atendimento não informou contatos por origem."}`
              : "Ainda não há vendas com origem informada.",
            actions: h(Button, { size: "sm", onClick: () => downloadCsv("vendas-por-origem.csv", acquisitionCsv(buckets)), "data-acquisition-action": "csv" }, "Exportar CSV")
          }),
          h(
            "div",
            { className: "overflow-x-auto" },
            h(
              "table",
              { className: "w-full text-left text-sm", "data-table": "origem-aquisicao" },
              h(
                "thead",
                null,
                h(
                  "tr",
                  { className: "text-xs font-extrabold text-muted" },
                  ["Origem", "Contatos", "Vendas", "Faturamento", "Conversão", "Participação", ""].map((title, index) =>
                    h("th", { key: index, scope: "col", className: "py-2 pr-3" }, title)
                  )
                )
              ),
              h(
                "tbody",
                null,
                buckets.map((bucket) =>
                  h(
                    "tr",
                    { key: bucket.key, className: "border-t border-border", "data-acquisition-origin": bucket.key },
                    h("th", { scope: "row", className: "py-2 pr-3 font-bold text-ink" }, bucket.label),
                    h("td", { className: "py-2 pr-3 tabular-nums" }, bucket.contacts === null ? "—" : formatCount(bucket.contacts)),
                    h("td", { className: "py-2 pr-3 tabular-nums" }, formatCount(bucket.sales)),
                    h("td", { className: "py-2 pr-3 tabular-nums" }, formatBrl(bucket.revenueCents)),
                    h("td", { className: "py-2 pr-3 tabular-nums" }, pct(bucket.conversion)),
                    h("td", { className: "py-2 pr-3 tabular-nums" }, pct(bucket.revenueShare)),
                    h("td", { className: "py-2" }, h(Link, { to: `/pedidos?origem=${encodeURIComponent(bucket.key)}`, className: "font-semibold text-brand-text" }, "Ver pedidos"))
                  )
                )
              )
            )
          )
        ),
    h(
      Card,
      { "data-panel": "estrategias" },
      h(CardHeader, { title: "Estratégias e campanhas", description: "Vendas que informaram campanha ou estratégia. O que está funcionando, por faturamento." }),
      strategies.length === 0
        ? h(EmptyState, { title: "Nenhuma estratégia registrada", description: "Preencha o campo de estratégia ao registrar ou marcar uma venda." })
        : h(
            "ul",
            { className: "m-0 flex list-none flex-col divide-y divide-border p-0" },
            strategies.map((strategy) =>
              h(
                "li",
                { key: strategy.strategy, className: "flex items-center justify-between gap-3 py-2 text-sm", "data-strategy": strategy.strategy },
                h("span", { className: "min-w-0 flex-1 font-semibold text-ink" }, strategy.strategy),
                h("span", { className: "tabular-nums text-muted" }, `${formatCount(strategy.sales)} venda(s)`),
                h("span", { className: "font-bold tabular-nums text-ink" }, formatBrl(strategy.revenueCents))
              )
            )
          )
    )
  );
}

/** Cards do dashboard: principal origem por vendas e por faturamento, e a participação da líder. */
export function AcquisitionLeaderCards({ orders, options }: AcquisitionPanelsProps): ReactElement {
  const buckets = acquisitionBuckets(orders, { options });
  const leaders = leadingOrigin(buckets);
  const missing = unsetSales(buckets);
  const cell = (label: string, key: string, value: string, hint: string): ReactElement =>
    h(
      Card,
      { key, "data-acquisition-card": key },
      h("p", { className: "m-0 text-sm font-extrabold text-muted" }, label),
      h("p", { className: `m-0 mt-1 text-xl font-black ${value === "Sem dados" ? "text-muted" : "text-ink"}`, "data-acquisition-value": key }, value),
      h("p", { className: "m-0 mt-1 text-xs text-muted" }, hint)
    );
  return h(
    "section",
    { className: "flex flex-col gap-3", "aria-label": "Origem das vendas", "data-section": "origem-lideres" },
    h("h2", { className: "text-lg font-extrabold text-ink" }, "De onde vêm as vendas"),
    h(
      "div",
      { className: "grid grid-cols-1 gap-4 sm:grid-cols-3" },
      cell("Principal origem por vendas", "por-vendas", leaders.bySales?.label ?? "Sem dados", leaders.bySales ? `${formatCount(leaders.bySales.sales)} venda(s) no período` : "Nenhuma venda com origem informada"),
      cell("Principal origem por faturamento", "por-faturamento", leaders.byRevenue?.label ?? "Sem dados", leaders.byRevenue ? formatBrl(leaders.byRevenue.revenueCents) : "Nenhuma venda com origem informada"),
      cell("Participação da principal origem", "participacao", leaders.leaderSalesShare === null ? "Sem dados" : pct(leaders.leaderSalesShare), "Percentual das vendas da origem líder")
    ),
    missing.sales > 0
      ? h(
          "p",
          { className: "m-0 text-xs text-muted", "data-acquisition": "sem-origem" },
          `${formatCount(missing.sales)} de ${formatCount(missing.totalSales)} venda(s) sem origem informada ficam fora do ranking. `,
          h(Link, { to: `/pedidos?origem=${UNSET_ORIGIN_KEY}`, className: "font-bold text-brand-text" }, "Marcar agora")
        )
      : null
  );
}
